import React, { Dispatch, SetStateAction, useEffect } from 'react';
import {
  TypedUseSelectorHook,
  useDispatch,
  useSelector as useReduxSelector,
} from 'react-redux';

import { get } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import SmsFailedRoundedIcon from '@mui/icons-material/SmsFailedRounded';
import { Box, Button, Divider, Drawer, Typography } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';

import { RootState } from '../../../../app/rootReducer';
import { AppDispatch } from '../../../../app/store';
import { FlexRow, NoraButton, Spacer } from '../../../../common/components/v2';
import { Resource } from '../../../../common/constants/resource';
import { EvidenceOrder } from '../../../tasks/slice/evidenceOrder';
import { orderTaskByTaskType } from '../../../tasks/slice/taskThunks';
import { MIBCodeback } from '../../../tasks/types/mibCodeback';
import { TaskTypes } from '../../../tasks/types/taskTypes';
import styles from '../styles.module.scss';
import { validateMIBCodes } from './CodebackSlice';

const SUBMIT_CODEBACK_BUTTON_NAME = 'codeback-drawer-submit-button';

const useSelector: TypedUseSelectorHook<RootState> = useReduxSelector;

export const ReviewCodebacks = ({
  applicationId,
  isOpen,
  setIsOpen,
  setIsReview,
  mibCodes,
  openSnacks,
  updateSnacks,
}: {
  applicationId: string;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  setIsReview: Dispatch<SetStateAction<boolean>>;
  mibCodes: string[];
  openSnacks: any;
  updateSnacks: (updatedSnacks: any) => void;
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const codeback = useSelector(state => state.codeback);
  let resultMap: Map<string, object>;

  useEffect(() => {
    if (mibCodes?.length > 0) {
      resultMap = new Map();
      dispatch(validateMIBCodes(applicationId, mibCodes));
    }
  }, [mibCodes]);

  function getValidationResult(code: string): any {
    if (resultMap?.size > 0 === false) {
      resultMap = new Map();
      const validationResult = get(
        codeback,
        'validationResult.validationResult',
        [],
      );
      if (
        validationResult &&
        get(validationResult, 'disabled') === 'disabled'
      ) {
        return <></>;
      } else if (validationResult && validationResult instanceof Array) {
        validationResult?.forEach((row: any[]) => {
          if (row?.length > 1) {
            resultMap.set(row[0], row[1]);
          }
        });
      }
    }

    const result = resultMap.get(code) || {};
    if (get(result, 'isValid') === true) {
      return (
        <Tooltip title={'Valid code'}>
          <CheckRoundedIcon
            color={'success'}
            sx={{ marginLeft: '15px', marginBottom: '-5px' }}
          ></CheckRoundedIcon>
        </Tooltip>
      );
    } else if (get(result, 'isValid') === false) {
      const messages = get(result, 'messages', []).join('; ');
      return (
        <Tooltip title={'Invalid code, you could still submit: ' + messages}>
          <ClearRoundedIcon
            color={'error'}
            sx={{ marginLeft: '15px', marginBottom: '-5px' }}
          ></ClearRoundedIcon>
        </Tooltip>
      );
    }
    return <></>;
  }

  return (
    <Drawer
      open={isOpen}
      anchor="right"
      variant="persistent"
      classes={{ paper: styles.Drawer }}
      transitionDuration={{ enter: 0, exit: 0 }}
    >
      <Box mt={6}>
        <Box
          m={3}
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="body1">
            {Resource.TASK_TITLE_ORDER_MIB_CODEBACK}
          </Typography>
          <Button onClick={() => setIsReview(false)}>
            <Typography variant="subtitle2" color="textSecondary">
              Back
            </Typography>
          </Button>
        </Box>
        <Divider />
        <Box m={3}>
          {mibCodes && mibCodes.length && (
            <>
              <FlexRow className={styles.DrawerSubTitle}>
                {Resource.MIB_CODES}
              </FlexRow>
              <Spacer height={8} />
              {mibCodes.map(mibCode => (
                <FlexRow key={uuidv4()}>
                  <span className={styles.Info}>
                    {`# ${mibCode}`}
                    {get(codeback, 'validationFailed', false) ? (
                      <Tooltip
                        title={'Validation failed, you could still submit'}
                      >
                        <SmsFailedRoundedIcon
                          fontSize={'small'}
                          color={'info'}
                          sx={{ marginLeft: '15px', marginBottom: '-5px' }}
                        ></SmsFailedRoundedIcon>
                      </Tooltip>
                    ) : (
                      getValidationResult(mibCode)
                    )}
                  </span>
                </FlexRow>
              ))}
              <Spacer height={8} />
            </>
          )}
        </Box>
        <Box>
          <NoraButton
            name={SUBMIT_CODEBACK_BUTTON_NAME}
            className={styles.DrawerButton}
            onClick={() => {
              const mibCodebackTaskData = { codes: mibCodes } as MIBCodeback;
              const mibTaskType = TaskTypes.MIB_CODEBACK;
              const order = new EvidenceOrder(mibTaskType, mibCodebackTaskData);

              dispatch(
                orderTaskByTaskType(
                  applicationId,
                  mibTaskType,
                  order,
                  openSnacks,
                  updateSnacks,
                ),
              );
              setIsReview(false);
              setIsOpen(false);
            }}
          >
            Submit MIB Codes
          </NoraButton>
        </Box>
      </Box>
    </Drawer>
  );
};
